import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AsyncBoundary, Notice, ConfirmDialog, Drawer } from '@kollez/design-system';

import {
  useApprovalDetail, useRevokeApproval, useSupersedeApproval,
  type ApprovalStatus,
} from '@/lib/queries';

/**
 * /group/approvals/:id — one regulator approval, with:
 *   - identity: number, title, granted-to, gazette ref, validity window
 *   - subject: kind (College/University/Programme/Branch) + name + code, linked
 *   - lifecycle: status pill, supersession chain if any, revocation reason
 *   - actions (writers only): Revoke (needs a reason ≥5 chars) and Supersede
 *     (creates a fresh Active row and marks this one Superseded atomically)
 *
 * Non-writers see the read view with the action rail disabled and a "read
 * only for your role" line — clearer than hiding the buttons entirely.
 */

const STATUS_LABEL: Record<string, string> = {
  active: 'Active',
  expired: 'Expired',
  revoked: 'Revoked',
  superseded: 'Superseded',
  pending_renewal: 'Pending renewal',
  withdrawn: 'Withdrawn',
};

function statusTone(s: string): string {
  if (s === 'active') return 'badge-success';
  if (s === 'expired' || s === 'pending_renewal') return 'badge-warning';
  if (s === 'revoked' || s === 'withdrawn') return 'badge-danger';
  return '';
}

function fmt(iso?: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
}

function subjectHref(kind: string | undefined, id: string | undefined): string | null {
  if (!kind || !id) return null;
  if (kind === 'College') return `/group/colleges/${id}`;
  if (kind === 'Program') return `/group/programs/${id}`;
  if (kind === 'Specialization') return `/group/specializations/${id}`;
  // University detail page not shipped yet; go back to structure.
  return null;
}

export function GroupApprovalDetailPage() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const { data, isLoading, error, refetch } = useApprovalDetail(id);
  const revoke = useRevokeApproval();
  const supersede = useSupersedeApproval();

  const [revokeOpen, setRevokeOpen] = useState(false);
  const [revokeReason, setRevokeReason] = useState('');
  const [supersedeOpen, setSupersedeOpen] = useState(false);
  const [supersedeForm, setSupersedeForm] = useState({
    approvalNumber: '',
    validFrom: '',
    validUntil: '',
    title: '',
    gazetteRef: '',
    certificateUrl: '',
    remarks: '',
  });

  const a = data?.approval;
  const isActive = a?.status === 'active';
  const canWrite = Boolean(a); // Server enforces role; we render + let 403 speak.

  async function submitRevoke() {
    if (!id) return;
    if (revokeReason.trim().length < 5) return;
    try {
      await revoke.mutateAsync({ id, reason: revokeReason.trim() });
      setRevokeOpen(false);
      setRevokeReason('');
    } catch {
      /* toast surfaces via useMutation error state below */
    }
  }

  async function submitSupersede() {
    if (!id) return;
    const f = supersedeForm;
    const body: Parameters<typeof supersede.mutateAsync>[0]['body'] = {
      approvalNumber: f.approvalNumber.trim(),
      validFrom: f.validFrom,
      ...(f.validUntil ? { validUntil: f.validUntil } : {}),
      ...(f.title.trim() ? { title: f.title.trim() } : {}),
      ...(f.gazetteRef.trim() ? { gazetteRef: f.gazetteRef.trim() } : {}),
      ...(f.certificateUrl.trim() ? { certificateUrl: f.certificateUrl.trim() } : {}),
      ...(f.remarks.trim() ? { remarks: f.remarks.trim() } : {}),
    };
    try {
      const out = await supersede.mutateAsync({ id, body });
      setSupersedeOpen(false);
      // Navigate to the new active row; keeps the timeline coherent.
      nav(`/group/approvals/${out.approval._id}`);
    } catch {
      /* handled below */
    }
  }

  const subject = data?.subject;
  const href = subjectHref(subject?.kind, subject?.id);

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">
            <Link className="link" to="/group/approvals">← All approvals</Link>
          </p>
          <h1 className="h-page">{a?.title ?? a?.approvalNumber ?? 'Approval'}</h1>
          {a && (
            <p className="subhead">
              {a.regulator} · <span className={`badge ${statusTone(a.status)}`}>{STATUS_LABEL[a.status] ?? a.status}</span>
              {a.approvalNumber && <span className="ml-2">#{a.approvalNumber}</span>}
            </p>
          )}
        </div>
      </div>

      <AsyncBoundary
        kind="detail"
        loading={isLoading}
        error={error}
        empty={!isLoading && !a}
        emptyTitle="Approval not found"
        emptyHint="It may have been deleted or belongs to another tenant."
        onRetry={() => void refetch()}
      >
        {a && (
          <div className="grid gap-3 lg:grid-cols-3">
            <div className="card stack lg:col-span-2">
              <h2 className="h-section">Letter</h2>
              <dl className="dl">
                <dt>Approval number</dt><dd>{a.approvalNumber}</dd>
                <dt>Regulator</dt><dd>{a.regulator}</dd>
                <dt>Title</dt><dd>{a.title ?? '—'}</dd>
                <dt>Granted to</dt><dd>{a.grantedTo ?? '—'}</dd>
                <dt>Valid from</dt><dd>{fmt(a.validFrom)}</dd>
                <dt>Valid until</dt><dd>{a.validTo ? fmt(a.validTo) : 'Open-ended'}</dd>
                <dt>Gazette ref</dt><dd>{a.gazetteRef ?? '—'}</dd>
                <dt>Certificate</dt>
                <dd>
                  {a.certificateUrl
                    ? <a className="link" href={a.certificateUrl} target="_blank" rel="noreferrer">Open PDF</a>
                    : '—'}
                </dd>
                {a.remarks && (<>
                  <dt>Remarks</dt><dd>{a.remarks}</dd>
                </>)}
                {a.status === 'revoked' && a.revokedReason && (<>
                  <dt>Revocation reason</dt><dd>{a.revokedReason}</dd>
                </>)}
              </dl>

              {data.supersededBy && (
                <Notice tone="info">
                  This row was <b>superseded</b> by approval <b>#{data.supersededBy.approvalNumber}</b>{' '}
                  (valid from {fmt(data.supersededBy.validFrom)}){' — '}
                  <Link className="link" to={`/group/approvals/${data.supersededBy._id}`}>
                    open current active
                  </Link>.
                </Notice>
              )}
            </div>

            <div className="card stack">
              <h2 className="h-section">Subject</h2>
              {subject ? (
                <>
                  <p className="eyebrow">{subject.kind}</p>
                  <div className="cell-title">{subject.name}</div>
                  {subject.code && <div className="cell-sub">Code · {subject.code}</div>}
                  {href && (
                    <Link className="btn btn-outline mt-3" to={href}>
                      Open {subject.kind.toLowerCase()}
                    </Link>
                  )}
                </>
              ) : (
                <p className="helper">Subject row could not be resolved.</p>
              )}

              <hr className="my-3" style={{ borderColor: 'rgb(var(--line-tertiary))' }} />

              <h3 className="h-section">Actions</h3>
              {isActive ? (
                <>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => setSupersedeOpen(true)}
                    disabled={!canWrite || supersede.isPending}
                  >
                    Supersede with new letter
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={() => setRevokeOpen(true)}
                    disabled={!canWrite || revoke.isPending}
                    style={{ color: 'rgb(var(--ink-danger))' }}
                  >
                    Revoke
                  </button>
                  <p className="helper mt-2">
                    Supersede keeps history and creates a new active row.
                    Revoke sets this row to <i>Revoked</i> — schema requires a reason.
                  </p>
                </>
              ) : (
                <Notice tone="info">
                  Actions available only on <b>Active</b> approvals. Current status: {STATUS_LABEL[a.status] ?? a.status}.
                </Notice>
              )}
              {(revoke.error || supersede.error) && (
                <Notice tone="warning">
                  {(revoke.error?.message ?? supersede.error?.message ?? 'Action failed')}
                </Notice>
              )}
            </div>
          </div>
        )}
      </AsyncBoundary>

      {/* Revoke confirm */}
      <ConfirmDialog
        open={revokeOpen}
        onOpenChange={setRevokeOpen}
        title="Revoke this approval?"
        confirmLabel={revoke.isPending ? 'Revoking…' : 'Revoke'}
        tone="danger"
        onConfirm={submitRevoke}
      >
        <p className="mb-2">
          The row stays in history with status <b>Revoked</b>. A fresh regulator letter can be recorded later.
        </p>
        <label className="grid gap-1.5">
          <span className="label">Reason (min 5 chars)</span>
          <textarea
            className="input"
            rows={3}
            value={revokeReason}
            onChange={(e) => setRevokeReason(e.target.value)}
            placeholder="e.g. AICTE revoked pending capacity audit — letter dated 12 Aug 2026."
          />
        </label>
      </ConfirmDialog>

      {/* Supersede drawer — a form, not a confirm */}
      <Drawer open={supersedeOpen} onClose={() => setSupersedeOpen(false)} title="Supersede with a new letter" size="lg">
        <p className="mb-3">
          The current row will be marked <b>Superseded</b>, and a new <b>Active</b> row will be
          created with the same subject + regulator. Only one active row per (subject, regulator)
          exists at a time; the switch is atomic.
        </p>
        <div className="grid gap-3 md:grid-cols-2">
          <label className="grid gap-1.5">
            <span className="label">New approval number *</span>
            <input
              className="input"
              value={supersedeForm.approvalNumber}
              onChange={(e) => setSupersedeForm({ ...supersedeForm, approvalNumber: e.target.value })}
              placeholder="F.NO. 30-11/2026-AICTE-ADMIN"
            />
          </label>
          <label className="grid gap-1.5">
            <span className="label">Valid from *</span>
            <input
              className="input"
              type="date"
              value={supersedeForm.validFrom}
              onChange={(e) => setSupersedeForm({ ...supersedeForm, validFrom: e.target.value })}
            />
          </label>
          <label className="grid gap-1.5">
            <span className="label">Valid until (blank for open-ended)</span>
            <input
              className="input"
              type="date"
              value={supersedeForm.validUntil}
              onChange={(e) => setSupersedeForm({ ...supersedeForm, validUntil: e.target.value })}
            />
          </label>
          <label className="grid gap-1.5">
            <span className="label">Title</span>
            <input
              className="input"
              value={supersedeForm.title}
              onChange={(e) => setSupersedeForm({ ...supersedeForm, title: e.target.value })}
              placeholder={a?.title ?? ''}
            />
          </label>
          <label className="grid gap-1.5">
            <span className="label">Gazette ref</span>
            <input
              className="input"
              value={supersedeForm.gazetteRef}
              onChange={(e) => setSupersedeForm({ ...supersedeForm, gazetteRef: e.target.value })}
            />
          </label>
          <label className="grid gap-1.5">
            <span className="label">Certificate URL</span>
            <input
              className="input"
              value={supersedeForm.certificateUrl}
              onChange={(e) => setSupersedeForm({ ...supersedeForm, certificateUrl: e.target.value })}
            />
          </label>
        </div>
        <label className="grid gap-1.5 mt-3">
          <span className="label">Remarks</span>
          <textarea
            className="input"
            rows={2}
            value={supersedeForm.remarks}
            onChange={(e) => setSupersedeForm({ ...supersedeForm, remarks: e.target.value })}
          />
        </label>
        <div className="drawer-foot">
          <button className="btn btn-ghost" onClick={() => setSupersedeOpen(false)}>Cancel</button>
          <button
            className="btn btn-primary ml-auto"
            disabled={
              supersede.isPending ||
              !supersedeForm.approvalNumber.trim() ||
              !supersedeForm.validFrom
            }
            onClick={submitSupersede}
          >
            {supersede.isPending ? 'Superseding…' : 'Supersede'}
          </button>
        </div>
      </Drawer>
    </div>
  );
}

// Local narrowing — the shared type declares status as ApprovalStatus but the
// server has a wider enum on Approval; the map above covers both worlds.
export type { ApprovalStatus };
